import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { addFavorite, createOrGetChat, listCategories, listCategoryAttributes, listListings, listMyFavoriteListingIds, removeFavorite } from "../lib/api";
import type { Category, CategoryAttribute, Listing } from "../types/domain";
import { ListingCard } from "../components/ListingCard";
import { LocationPicker } from "../components/LocationPicker";
import { CategoryTreePicker } from "../components/CategoryTreePicker";
import { categoryPath, childrenOf, isConditionApplicableForCategories } from "../lib/categories";
import { StandardSelect } from "../components/StandardSelect";
import { CategoryCascadeSelect } from "../components/CategoryCascadeSelect";
import { Notice } from "../components/Notice";
import { Icon } from "../components/Icon";
import { useAuth } from "../lib/auth";
import { readSearchSession, writeSearchSession } from "../lib/searchSession";

const AUTO_BRANDS = [
  "Audi", "BMW", "Changan", "Chery", "Chevrolet", "Daewoo", "Ford", "Geely",
  "Haval", "Honda", "Hyundai", "Kia", "Lexus", "Mazda", "Mercedes-Benz", "Mitsubishi",
  "Nissan", "OMODA", "Opel", "Peugeot", "Renault", "Skoda", "Toyota", "Volkswagen",
  "ВАЗ (LADA)", "ГАЗ", "УАЗ"
];

const CAR_BODY_TYPES = ["Седан", "Внедорожник 5-дверный", "Внедорожник 3-дверный", "Универсал", "Хетчбек 5-дверный", "Хетчбек 3-дверный", "Купе", "Минивэн", "Пикап", "Фургон", "Кабриолет"];

const AUTO_TRANSMISSIONS = ["Механика", "Автомат", "Вариатор", "Робот"];
const AUTO_DRIVES = ["Передний", "Задний", "Полный"];
const AUTO_ENGINES = ["Бензин", "Газ", "Гибрид", "Дизель", "Электро"];
const AUTO_SEATS = ["2", "3", "4", "5", "6", "7", "8+"];
const SORT_OPTIONS = [{ value: "newest", label: "Сначала новые" }, { value: "price_asc", label: "Дешевле" }, { value: "price_desc", label: "Дороже" }];

export function CatalogPage({ params, go }: { params: URLSearchParams; go: (route: string, params?: Record<string, string>) => void }) {
  const { user } = useAuth();
  const paramsKey = params.toString();
  const [initialSearch] = useState(() => readSearchSession());
  const hasSearchParams = params.has("q") || params.has("category") || params.has("country") || params.has("cities") || params.has("regions");
  const [categories, setCategories] = useState<Category[]>([]);
  const [attributes, setAttributes] = useState<CategoryAttribute[]>([]);
  const [listings, setListings] = useState<Listing[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(() => new Set());
  const [favoriteBusyIds, setFavoriteBusyIds] = useState<Set<number>>(() => new Set());
  const [query, setQuery] = useState(hasSearchParams ? params.get("q") ?? "" : initialSearch?.query ?? "");
  const [categoryId, setCategoryId] = useState<number | null>(hasSearchParams ? params.get("category") ? Number(params.get("category")) : null : initialSearch?.categoryId ?? null);
  const [countryCode, setCountryCode] = useState<string | null>(hasSearchParams ? params.get("country") || null : initialSearch?.countryCode ?? null);
  const [cities, setCities] = useState<string[]>(hasSearchParams ? params.get("cities")?.split(",").filter(Boolean) ?? [] : initialSearch?.cities ?? []);
  const [regions, setRegions] = useState<string[]>(hasSearchParams ? params.get("regions")?.split(",").filter(Boolean) ?? [] : initialSearch?.regions ?? []);
  const [condition, setCondition] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sort, setSort] = useState<"newest" | "price_asc" | "price_desc">("newest");
  const [attributeFilters, setAttributeFilters] = useState<Record<string, string | number | boolean | null>>({});
  const [brandQuery, setBrandQuery] = useState("");
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [autoFilters, setAutoFilters] = useState<Record<string, string>>({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const listingIdsKey = listings.map((listing) => listing.id).join("|");

  useEffect(() => {
    void listCategories().then(setCategories).catch((error) => console.warn(error));
  }, []);

  useEffect(() => {
    const nextHasSearchParams = params.has("q") || params.has("category") || params.has("country") || params.has("cities") || params.has("regions");
    if (nextHasSearchParams) {
      setQuery(params.get("q") ?? "");
      setCategoryId(params.get("category") ? Number(params.get("category")) : null);
      setCountryCode(params.get("country") || null);
      setCities(params.get("cities")?.split(",").filter(Boolean) ?? []);
      setRegions(params.get("regions")?.split(",").filter(Boolean) ?? []);
      return;
    }
    const nextSession = readSearchSession();
    setQuery(nextSession?.query ?? "");
    setCategoryId(nextSession?.categoryId ?? null);
    setCountryCode(nextSession?.countryCode ?? null);
    setCities(nextSession?.cities ?? []);
    setRegions(nextSession?.regions ?? []);
  }, [paramsKey]);

  useEffect(() => {
    writeSearchSession({ query, categoryId, countryCode, cities, regions });
  }, [query, categoryId, countryCode, cities.join("|"), regions.join("|")]);

  useEffect(() => {
    void listCategoryAttributes(categoryId).then(setAttributes);
    setAttributeFilters({});
  }, [categoryId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    void listListings({
      query,
      categoryId,
      countryCode,
      cities,
      regions,
      condition: condition || null,
      priceMin: priceMin ? Number(priceMin) : null,
      priceMax: priceMax ? Number(priceMax) : null,
      sort,
      attributeFilters
    })
      .then((nextListings) => {
        if (alive) setListings(nextListings);
      })
      .catch((error) => {
        if (!alive) return;
        console.warn(error);
        setMessage("Не удалось загрузить объявления.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [query, categoryId, countryCode, cities.join("|"), regions.join("|"), condition, priceMin, priceMax, sort, JSON.stringify(attributeFilters)]);

  useEffect(() => {
    if (!user || !listings.length) {
      setFavoriteIds(new Set());
      return;
    }
    let alive = true;
    void listMyFavoriteListingIds(listings.map((listing) => listing.id))
      .then((ids) => {
        if (alive) setFavoriteIds(new Set(ids));
      })
      .catch((error) => console.warn(error));
    return () => {
      alive = false;
    };
  }, [user?.id, listingIdsKey]);

  const path = useMemo(() => categoryPath(categories, categoryId), [categories, categoryId]);
  const conditionApplicable = isConditionApplicableForCategories(path);
  const pathSlugs = path.map((category) => category.slug);
  const heading = path[path.length - 1]?.name ?? "Все категории";
  const isAutoCategory = pathSlugs.includes("cars") || pathSlugs.includes("cars-used") || pathSlugs.includes("cars-new");
  const autoTypeSlug = pathSlugs.includes("cars-used") ? "cars-used" : pathSlugs.includes("cars-new") ? "cars-new" : pathSlugs.includes("cars") ? "cars" : null;
  const currentSlug = path[path.length - 1]?.slug;
  const categoryBySlug = new Map(categories.map((category) => [category.slug, category]));

  useEffect(() => {
    if (!conditionApplicable && condition) setCondition("");
  }, [conditionApplicable, condition]);
  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const nextParams: Record<string, string> = {};
    if (query.trim()) nextParams.q = query.trim();
    if (categoryId) nextParams.category = String(categoryId);
    if (countryCode) nextParams.country = countryCode;
    if (cities.length) nextParams.cities = cities.join(",");
    if (regions.length) nextParams.regions = regions.join(",");
    writeSearchSession({ query: query.trim(), categoryId, countryCode, cities, regions });
    go("catalog", nextParams);
  };

  const openFinalCategory = (nextCategoryId: number) => {
    const nextParams: Record<string, string> = {};
    if (query.trim()) nextParams.q = query.trim();
    nextParams.category = String(nextCategoryId);
    if (countryCode) nextParams.country = countryCode;
    if (cities.length) nextParams.cities = cities.join(",");
    if (regions.length) nextParams.regions = regions.join(",");
    writeSearchSession({ query: query.trim(), categoryId: nextCategoryId, countryCode, cities, regions });
    go("catalog", nextParams);
  };

  const openCategorySlug = (slug: string) => {
    const category = categoryBySlug.get(slug);
    if (category) openFinalCategory(category.id);
  };

  const setAutoFilter = (key: string, value: string) => {
    setAutoFilters((current) => ({ ...current, [key]: current[key] === value ? "" : value }));
  };

  const brandCounts = useMemo(() => {
    const counts = new Map(AUTO_BRANDS.map((brand) => [brand, 0]));
    listings.forEach((listing) => {
      const title = listing.title.toLowerCase();
      const rawBrand = String(listing.attributes?.brand ?? "").toLowerCase();
      const brand = AUTO_BRANDS.find((item) => rawBrand === item.toLowerCase() || title.includes(item.toLowerCase()));
      if (brand) counts.set(brand, (counts.get(brand) ?? 0) + 1);
    });
    return counts;
  }, [listings]);

  const visibleBrands = (showAllBrands ? AUTO_BRANDS : AUTO_BRANDS.slice(0, 16)).filter((brand) => brand.toLowerCase().includes(brandQuery.trim().toLowerCase()));

  const renderSegmented = (key: string, values: string[]) => (
    <div className="auto-segmented">
      {values.map((value) => (
        <button className={autoFilters[key] === value ? "active" : ""} type="button" onClick={() => setAutoFilter(key, value)} key={value}>{value}</button>
      ))}
    </div>
  );

  const renderAutoFilters = () => {
    if (!isAutoCategory) return null;
    return (
      <div className="auto-filters">
        <section>
          <h3>Тип автомобиля</h3>
          <div className="auto-segmented">
            <button className={autoTypeSlug === "cars" ? "active" : ""} type="button" onClick={() => openCategorySlug("cars")}>Все</button>
            <button className={autoTypeSlug === "cars-used" ? "active" : ""} type="button" onClick={() => openCategorySlug("cars-used")}>С пробегом</button>
            <button className={autoTypeSlug === "cars-new" ? "active" : ""} type="button" onClick={() => openCategorySlug("cars-new")}>Новые</button>
          </div>
        </section>
        <section>
          <h3>Страна бренда</h3>
          {["Отечественные", "Иномарки", "Китай", "Корея", "Япония"].map((value) => (
            <label className="check auto-check" key={value}><input type="checkbox" checked={autoFilters.brandCountry === value} onChange={() => setAutoFilter("brandCountry", value)} />{value}</label>
          ))}
        </section>
        <label>Марка<input value={brandQuery} onChange={(event) => setBrandQuery(event.target.value)} placeholder="Любая" /></label>
        <div className="two">
          <label>Год выпуска от<input type="number" value={autoFilters.yearFrom ?? ""} onChange={(event) => setAutoFilters((current) => ({ ...current, yearFrom: event.target.value }))} /></label>
          <label>до<input type="number" value={autoFilters.yearTo ?? ""} onChange={(event) => setAutoFilters((current) => ({ ...current, yearTo: event.target.value }))} /></label>
        </div>
        <div className="two">
          <label>Пробег, км от<input type="number" value={autoFilters.mileageFrom ?? ""} onChange={(event) => setAutoFilters((current) => ({ ...current, mileageFrom: event.target.value }))} /></label>
          <label>до<input type="number" value={autoFilters.mileageTo ?? ""} onChange={(event) => setAutoFilters((current) => ({ ...current, mileageTo: event.target.value }))} /></label>
        </div>
        <section><h3>Коробка передач</h3>{renderSegmented("transmission", AUTO_TRANSMISSIONS)}</section>
        <section><h3>Привод</h3>{renderSegmented("drive", AUTO_DRIVES)}</section>
        <section><h3>Тип двигателя</h3>{renderSegmented("engine", AUTO_ENGINES)}</section>
        <section><h3>Турбина</h3>{renderSegmented("turbo", ["Не важно", "Есть", "Нет"])}</section>
        <div className="two">
          <label>Объём от<input value={autoFilters.engineVolumeFrom ?? ""} onChange={(event) => setAutoFilters((current) => ({ ...current, engineVolumeFrom: event.target.value }))} /></label>
          <label>до<input value={autoFilters.engineVolumeTo ?? ""} onChange={(event) => setAutoFilters((current) => ({ ...current, engineVolumeTo: event.target.value }))} /></label>
        </div>
        <div className="two">
          <label>Мощность от<input type="number" value={autoFilters.powerFrom ?? ""} onChange={(event) => setAutoFilters((current) => ({ ...current, powerFrom: event.target.value }))} /></label>
          <label>до<input type="number" value={autoFilters.powerTo ?? ""} onChange={(event) => setAutoFilters((current) => ({ ...current, powerTo: event.target.value }))} /></label>
        </div>
        <section><h3>Состояние</h3>{renderSegmented("damage", ["Все", "Кроме битых", "Битые"])}</section>
        <section>
          <h3>Кузов</h3>
          <div className="auto-body-grid">
            {CAR_BODY_TYPES.map((value) => <button className={autoFilters.body === value ? "active" : ""} type="button" onClick={() => setAutoFilter("body", value)} key={value}>{value}</button>)}
          </div>
        </section>
        <section><h3>Количество мест</h3>{renderSegmented("seats", AUTO_SEATS)}</section>
      </div>
    );
  };

  const renderAutoHub = () => {
    if (!isAutoCategory) return null;
    return (
      <section className="auto-hub">
        <div className="section-title-row auto-hub-title">
          <h2>{listings.length ? "Популярные легковые автомобили" : "Марки автомобилей"}</h2>
          <button type="button" onClick={() => setShowAllBrands((value) => !value)}>{showAllBrands ? "Свернуть" : "Все"}</button>
        </div>
        <div className="auto-brand-grid">
          {visibleBrands.map((brand) => (
            <button type="button" onClick={() => { setBrandQuery(brand); setAttributeFilters((current) => ({ ...current, brand })); }} key={brand}>
              {brand} <span>{(brandCounts.get(brand) ?? 0).toLocaleString("ru-RU")}</span>
            </button>
          ))}
        </div>
      </section>
    );
  };

  const openChat = async (listing: Listing) => {
    setMessage(null);
    try {
      const chat = await createOrGetChat(listing);
      go("chat", { id: String(chat.id) });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Не удалось открыть чат.");
    }
  };

  const toggleFavorite = async (listing: Listing) => {
    if (!user) {
      go("login");
      return;
    }
    if (listing.user_id === user.id || favoriteBusyIds.has(listing.id)) return;
    setMessage(null);
    const wasFavorite = favoriteIds.has(listing.id);
    setFavoriteBusyIds((current) => new Set(current).add(listing.id));
    setFavoriteIds((current) => {
      const next = new Set(current);
      if (wasFavorite) next.delete(listing.id);
      else next.add(listing.id);
      return next;
    });
    try {
      if (wasFavorite) await removeFavorite(listing.id);
      else await addFavorite(listing.id);
    } catch (error) {
      console.warn(error);
      setFavoriteIds((current) => {
        const next = new Set(current);
        if (wasFavorite) next.add(listing.id);
        else next.delete(listing.id);
        return next;
      });
      setMessage("Не удалось обновить избранное.");
    } finally {
      setFavoriteBusyIds((current) => {
        const next = new Set(current);
        next.delete(listing.id);
        return next;
      });
    }
  };

  const renderCoreFilters = () => (
    <>
      {renderAutoFilters()}
      <div className="price-filter-group">
        <span className="filter-label">Цена</span>
        <div className="two price-filter">
        <label><span className="price-filter-label">Цена от</span><input type="number" value={priceMin} placeholder="От" onChange={(event) => setPriceMin(event.target.value)} /></label>
        <label><span className="price-filter-label">до</span><input type="number" value={priceMax} placeholder="До" onChange={(event) => setPriceMax(event.target.value)} /></label>
        </div>
      </div>
      {conditionApplicable && !isAutoCategory ? <label>Состояние
        <StandardSelect
          value={condition}
          options={[{ value: "", label: "Любое" }, { value: "new", label: "Новое" }, { value: "like_new", label: "Как новое" }, { value: "used", label: "Б/у" }]}
          onChange={setCondition}
        />
      </label> : null}
      {attributes.map(renderAttributeFilter)}
    </>
  );

  const renderAttributeFilter = (attribute: CategoryAttribute) => {
    if (!attribute.is_filterable) return null;
    const value = attributeFilters[attribute.key];
    if (attribute.type === "select") {
      return (
        <label key={attribute.id}>{attribute.label}
          <StandardSelect
            value={String(value ?? "")}
            options={[{ value: "", label: "Любое" }, ...(attribute.options ?? []).map((option) => ({ value: option, label: option }))]}
            onChange={(nextValue) => setAttributeFilters((current) => ({ ...current, [attribute.key]: nextValue || null }))}
          />
        </label>
      );
    }
    if (attribute.type === "number") {
      return (
        <label key={attribute.id}>{attribute.label}{attribute.unit ? `, ${attribute.unit}` : ""}
          <input type="number" value={String(value ?? "")} onChange={(event) => setAttributeFilters((current) => ({ ...current, [attribute.key]: Number(event.target.value) || null }))} />
        </label>
      );
    }
    return (
      <label key={attribute.id}>{attribute.label}
        <input value={String(value ?? "")} onChange={(event) => setAttributeFilters((current) => ({ ...current, [attribute.key]: event.target.value || null }))} />
      </label>
    );
  };

  const renderSortSelect = (className = "catalog-sort-select") => (
    <StandardSelect
      className={className}
      value={sort}
      options={SORT_OPTIONS}
      onChange={(value) => setSort(value as "newest" | "price_asc" | "price_desc")}
    />
  );

  return (
    <>
      <section className="search-strip catalog-search-strip">
        <button className="category-button catalog-desktop-category-button" type="button" onClick={() => setCategoryId(null)}><Icon name="grid" />Все категории</button>
        <form className="search-form" onSubmit={submitSearch}>
          <div className="catalog-search-field">
            <input placeholder="Поиск по объявлениям" value={query} onChange={(event) => setQuery(event.target.value)} />
            <button
              className={`catalog-search-filter-button${mobileFiltersOpen ? " active" : ""}`}
              type="button"
              onClick={() => setMobileFiltersOpen((open) => !open)}
              aria-label="Фильтры"
              aria-expanded={mobileFiltersOpen}
            >
              <Icon name="sliders" />
            </button>
          </div>
          <CategoryCascadeSelect categories={categories} value={categoryId} onChange={setCategoryId} />
          <LocationPicker allowAllCountries allowRegions countryCode={countryCode} cities={cities} regions={regions} className="search-location" onChange={(next) => { setCountryCode(next.countryCode); setCities(next.cities); setRegions(next.regions ?? []); }} />
          <div className={`filters catalog-mobile-filters${mobileFiltersOpen ? " mobile-open" : ""}`}>
            {renderCoreFilters()}
            <div className="catalog-mobile-sort">
              <span className="filter-label">Сортировка</span>
              {renderSortSelect("catalog-sort-select catalog-mobile-sort-select")}
            </div>
          </div>
          <button type="submit">Найти</button>
        </form>
      </section>
      <div className="page-head catalog-head">
        {path.length ? <p className="breadcrumb">
          <button type="button" onClick={() => setCategoryId(null)}>Все категории</button>
          {path.map((category) => <button type="button" key={category.id} onClick={() => setCategoryId(category.id)}>› {category.name}</button>)}
        </p> : null}
        <div className="catalog-title-row">
          <h1>{heading} <span className="catalog-count">{loading ? "" : listings.length.toLocaleString("ru-RU")}</span></h1>
          <div className="catalog-toolbar">
            {renderSortSelect()}
          </div>
        </div>
      </div>
      {message ? <Notice kind="error">{message}</Notice> : null}
      <div className="catalog-layout catalog-layout-rich">
        <aside className={`filters catalog-filters${mobileFiltersOpen ? " mobile-open" : ""}`}>
          <div className="catalog-filter-category">
            <span className="filter-label">Категория</span>
            <CategoryTreePicker categories={categories} value={categoryId} onChange={openFinalCategory} compact />
          </div>
          <LocationPicker allowAllCountries allowRegions countryCode={countryCode} cities={cities} regions={regions} className="filters-location catalog-filter-location" onChange={(next) => { setCountryCode(next.countryCode); setCities(next.cities); setRegions(next.regions ?? []); }} />
          {renderCoreFilters()}
        </aside>
        <section className="catalog-results">
          {renderAutoHub()}
          <div className="listing-list">
            {listings.map((listing) => (
              <ListingCard
                listing={listing}
                go={go}
                mode="row"
                onMessage={openChat}
                showFavorite={!user || listing.user_id !== user.id}
                isFavorite={favoriteIds.has(listing.id)}
                favoriteBusy={favoriteBusyIds.has(listing.id)}
                onFavoriteToggle={toggleFavorite}
                key={listing.id}
              />
            ))}
          </div>
          {!loading && !listings.length ? <p className="empty">Пока нет объявлений под эти фильтры.</p> : null}
        </section>
      </div>
    </>
  );
}
